import { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { useSettings } from "@/lib/useSettings";
import { api } from "@/lib/api";
import { Send, CheckCircle2, AlertCircle, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import SeoHead, { SITE_URL } from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import { SectionBand } from "@/components/SectionPrimitives";
import { buildContactPageSchema, buildLocalBusinessSchema } from "@/lib/schema";
import { EDITORIAL_PHOTOS } from "@/lib/editorialPhotos";

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-2">
    <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{label}</span>
    {children}
  </label>
);

const inputCls = "bg-white border-2 border-[#E6E0D5] focus:border-[#0B1210] px-4 py-3 rounded-xl text-[15px] font-bold-dl text-[#0B1210] outline-none transition-colors";

const Contact = () => {
  const { lang } = useLang();
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", topic: "", message: "", website: "", language: lang, source: "www" });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setStatus(null);
    try {
      await api.post("/contact", { ...form, language: lang });
      setStatus("ok");
      setForm({ name: "", email: "", company: "", role: "", topic: "", message: "", website: "", language: lang, source: "www" });
    } catch { setStatus("error"); } finally { setBusy(false); }
  };

  const TOPICS = lang === "fr" ? [
    { v: "", t: "- Choisir -" },
    { v: "Advisory & Transformation", t: "Advisory & Transformation" },
    { v: "APS / Planning Technology", t: "APS / Planning Technology" },
    { v: "Program / Project Delivery", t: "Program / Project Delivery" },
    { v: "Data / Integration", t: "Data / Integration" },
    { v: "Application Management", t: "Application Management (TMA)" },
    { v: "AI / Decision Apps", t: "IA / Decision Apps" },
    { v: "Labs / Demonstrator", t: "Labs / Démonstrateur" },
    { v: "Other", t: "Autre" },
  ] : [
    { v: "", t: "- Choose -" },
    { v: "Advisory & Transformation", t: "Advisory & Transformation" },
    { v: "APS / Planning Technology", t: "APS / Planning Technology" },
    { v: "Program / Project Delivery", t: "Program / Project Delivery" },
    { v: "Data / Integration", t: "Data / Integration" },
    { v: "Application Management", t: "Application Management" },
    { v: "AI / Decision Apps", t: "AI / Decision Apps" },
    { v: "Labs / Demonstrator", t: "Labs / Demonstrator" },
    { v: "Other", t: "Other" },
  ];

  const labels = lang === "fr" ? {
    title: "Parlons de votre projet.",
    kicker: "Contact",
    intro: "Une transformation Supply Chain, un programme APS, une Decision App à cadrer ? Échangeons sur vos enjeux et les prochaines étapes possibles.",
    name: "Nom", email: "Email professionnel", company: "Entreprise", role: "Fonction", topic: "Sujet", message: "Message",
    send: "Envoyer", sending: "Envoi…",
    ok: "Message envoyé. Nous revenons vers vous rapidement.",
    err: "Erreur lors de l'envoi. Réessayez ou écrivez-nous directement.",
    quickKicker: "Nous joindre",
    quickTitle: "Trois moyens de nous atteindre.",
  } : {
    title: "Let's talk about your project.",
    kicker: "Contact",
    intro: "A Supply Chain transformation, an APS program, a Decision App to frame? Let us discuss your priorities and possible next steps.",
    name: "Name", email: "Business email", company: "Company", role: "Role", topic: "Topic", message: "Message",
    send: "Send", sending: "Sending…",
    ok: "Message sent. We'll get back to you shortly.",
    err: "Something went wrong. Try again or reach us directly.",
    quickKicker: "Reach us",
    quickTitle: "Three ways to connect.",
  };

  return (
    <div data-testid="contact-page">
      <SeoHead
        lang={lang}
        path="/contact"
        title={labels.title}
        description={labels.intro}
        jsonLd={[
          buildContactPageSchema({ baseUrl: SITE_URL, settings }),
          buildLocalBusinessSchema({ baseUrl: SITE_URL, settings }),
        ]}
      />
      <PageHero
        tone="cream"
        kicker={labels.kicker}
        title={labels.title}
        description={labels.intro}

        testId="contact-hero"
      />

      {/* Three quick-reach tiles */}
      <SectionBand tone="cream" testId="contact-quick">
        <div className="grid sm:grid-cols-3 gap-5">
          {(settings.address_street || settings.address_city) && (
            <div className="bg-white rounded-[20px] border border-[#E6E0D5] p-6" data-testid="contact-address">
              <div className="w-11 h-11 rounded-full bg-[#00A878] text-white grid place-items-center">
                <MapPin size={18} />
              </div>
              <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#4B5563] mt-5">
                {lang === "fr" ? "Adresse" : "Address"}
              </div>
              <div className="mt-2 text-[15px] font-bold-dl text-[#0B1210]">{settings.address_street}</div>
              <div className="text-[14px] text-[#4B5563]">{[settings.address_postal_code, settings.address_city].filter(Boolean).join(" ")}</div>
              <div className="text-[14px] text-[#4B5563]">{settings.address_country}</div>
            </div>
          )}
          {settings.phone && (
            <div className="bg-white rounded-[20px] border border-[#E6E0D5] p-6" data-testid="contact-phone">
              <div className="w-11 h-11 rounded-full bg-[#00A878] text-white grid place-items-center">
                <Phone size={18} />
              </div>
              <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#4B5563] mt-5">
                {lang === "fr" ? "Téléphone" : "Phone"}
              </div>
              <a href={`tel:${settings.phone.replace(/\s+/g, "")}`} className="mt-2 inline-block text-[18px] font-bold-dl text-[#0B1210] hover:text-[#00A878]">
                {settings.phone}
              </a>
            </div>
          )}
          {settings.contact_email && (
            <div className="bg-white rounded-[20px] border border-[#E6E0D5] p-6">
              <div className="w-11 h-11 rounded-full bg-[#00A878] text-white grid place-items-center">
                <Mail size={18} />
              </div>
              <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#4B5563] mt-5">Email</div>
              <a href={`mailto:${settings.contact_email}`} className="mt-2 inline-block text-[16px] font-bold-dl text-[#0B1210] hover:text-[#00A878] break-all">
                {settings.contact_email}
              </a>
            </div>
          )}
        </div>
      </SectionBand>

      {/* Form on cream alt (#EFEAE0) */}
      <SectionBand tone="cream" className="bg-[#EFEAE0]" testId="contact-form-band">
        <div className="max-w-3xl bg-white rounded-[28px] p-8 sm:p-10 border border-[#E6E0D5]">
          <form onSubmit={submit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={`${labels.name} *`}>
                <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </Field>
              <Field label={`${labels.email} *`}>
                <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              </Field>
              <Field label={labels.company}>
                <input data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
              </Field>
              <Field label={labels.role}>
                <input data-testid="contact-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} />
              </Field>
              <div className="sm:col-span-2">
                <Field label={labels.topic}>
                  <select data-testid="contact-topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className={inputCls}>
                    {TOPICS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
                  </select>
                </Field>
              </div>
            </div>
            <Field label={`${labels.message} *`}>
              <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
            </Field>

            <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" />

            <div className="flex items-center gap-4 pt-3">
              <button
                type="submit"
                disabled={busy}
                data-testid="contact-submit"
                className="dl-pill dl-pill-primary disabled:opacity-60"
              >
                <Send size={14} strokeWidth={2.5} /> {busy ? labels.sending : labels.send}
                <ArrowRight size={14} strokeWidth={2.5} />
              </button>
              {status === "ok" && (
                <div data-testid="contact-ok" className="inline-flex items-center gap-2 text-[14px] font-bold-dl text-[#00A878]">
                  <CheckCircle2 size={18} /> {labels.ok}
                </div>
              )}
              {status === "error" && (
                <div data-testid="contact-error" className="inline-flex items-center gap-2 text-[14px] font-bold-dl text-red-700">
                  <AlertCircle size={18} /> {labels.err}
                </div>
              )}
            </div>
          </form>
        </div>
      </SectionBand>
    </div>
  );
};

export default Contact;
